import React, { Component, ErrorInfo, ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Catches React render errors in the tree and shows a friendly error page.
 * Wrap top-level routes so users see a clear message instead of a blank screen.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(_error: Error, _errorInfo: ErrorInfo): void {
    // Error is already in state; optionally report to a logging service here.
  }

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) return this.props.fallback;
      return <ErrorBoundaryFallback onReload={() => this.setState({ hasError: false, error: null })} />;
    }
    return this.props.children;
  }
}

function ErrorBoundaryFallback({ onReload }: { onReload: () => void }) {
  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center px-6 text-center">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl border border-red-500/30 bg-red-500/10 mb-6">
        <AlertTriangle className="w-10 h-10 text-red-400" aria-hidden />
      </div>
      <h1 className="text-2xl font-semibold text-white mb-2">Something went wrong</h1>
      <p className="text-gray-400 max-w-md mb-8">
        We’re sorry — something unexpected happened. You can try again or go back to the home page.
      </p>
      <div className="flex flex-wrap gap-3 justify-center">
        <button
          type="button"
          onClick={onReload}
          className="px-5 py-2.5 rounded-[27px] border-2 border-[#E761A0] bg-[#E761A0] text-white font-semibold hover:bg-transparent hover:text-[#E761A0] transition-colors"
        >
          Try again
        </button>
        <Link
          to="/"
          className="px-5 py-2.5 rounded-[27px] border-2 border-white/30 text-white font-semibold hover:bg-white/10 transition-colors"
        >
          Go to home
        </Link>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="px-5 py-2.5 rounded-[27px] border-2 border-white/20 text-gray-400 font-medium hover:text-white hover:border-white/40 transition-colors"
        >
          Reload page
        </button>
      </div>
    </div>
  );
}

export default ErrorBoundary;
